import { useCallback, useEffect, useState, type RefObject } from 'react'

/**
 * useEdgeFade — the "there's more, scroll" cue for a horizontal scroll row whose scrollbar is hidden (or
 * an overlay one, as on mobile). Returns a CSS mask-image string (or undefined) that fades the row's
 * content INTO its own background at whichever edge can still scroll.
 *
 * Color-agnostic on purpose: it masks to transparent, so it needs NO matching against the row's background
 * or any backdrop-blur behind it. No overflow -> undefined (no mask), so a row whose items all fit is left
 * untouched (e.g. the same tab bar on desktop vs mobile).
 *
 * Pass the SCROLLING element's ref. Recompute is driven by that element's own scroll + window resize, and
 * re-runs when `deps` change - pass whatever governs the content width (the item list, a data-settled flag)
 * so the edges are re-measured once layout lands.
 *
 * Usage:  const mask = useEdgeFade(scrollRef, [items])
 *         <div ref={scrollRef} style={{ maskImage: mask, WebkitMaskImage: mask }} className="overflow-x-auto ...">
 */
export function useEdgeFade(ref: RefObject<HTMLElement>, deps: unknown[] = []): string | undefined {
  const [edge, setEdge] = useState({ l: false, r: false })

  const update = useCallback(() => {
    const el = ref.current
    if (!el) return
    setEdge({ l: el.scrollLeft > 4, r: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 })
  }, [ref])

  useEffect(() => {
    update()
    const el = ref.current
    if (!el) return
    el.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => { el.removeEventListener('scroll', update); window.removeEventListener('resize', update) }
    // deps governs content width (item list / settle flag); update is stable per ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [update, ...deps])

  return edge.l && edge.r
    ? 'linear-gradient(to right, transparent, #000 28px, #000 calc(100% - 28px), transparent)'
    : edge.r ? 'linear-gradient(to right, #000 calc(100% - 28px), transparent)'
    : edge.l ? 'linear-gradient(to right, transparent, #000 28px)'
    : undefined
}
